import { rejectComponentId } from '../schemas/component-id';

import { parseMemberList, type ComponentDraft } from './component-create';

import type { CreateReleaseInput } from './release-components';

/**
 * The Release half of the Components dialog, as pure functions for the reason
 * `component-create.ts` gives: vitest runs on `node`, so a payload built inside
 * the dialog is a payload no test can see.
 *
 * The dialog keeps one draft for both types. A release reads the fields it
 * shares (`id`, `displayName`, `description`, `members`, `reason`) and ignores
 * the commercial-only ones, so switching the Type selector does not discard what
 * the operator typed.
 */

export type ComponentType = 'commercial' | 'release';

export type ReleaseDraft = Pick<
    ComponentDraft,
    'id' | 'displayName' | 'description' | 'members' | 'reason'
> & { readonly releaseVersion: string };

/** Mirrors `isReleaseVersion` on the API: 1-32 characters of `[A-Za-z0-9._-]`. */
const RELEASE_VERSION = /^[A-Za-z0-9._-]{1,32}$/;

export function isReleaseVersion(version: string): boolean {
    return RELEASE_VERSION.test(version.trim());
}

/**
 * The body for `POST /release-components`, or `null` while the draft cannot be
 * sent.
 *
 * No owner, owner service or activation path: the API's body is strict, so one
 * of them is a 400 rather than something silently dropped. Flags may be empty (a
 * release can be assembled first); the 50-flag cap is left to the API so there
 * is one source of the number.
 */
export function releaseCreateInputFrom(draft: ReleaseDraft): CreateReleaseInput | null {
    const id = draft.id.trim();
    const name = draft.displayName.trim();
    const description = draft.description.trim();
    const releaseVersion = draft.releaseVersion.trim();

    if (rejectComponentId(id) !== null) return null;
    if (name.length === 0 || description.length === 0) return null;
    if (!isReleaseVersion(releaseVersion)) return null;

    const reason = draft.reason.trim();

    return {
        id,
        name,
        description,
        releaseVersion,
        flagKeys: parseMemberList(draft.members),
        ...(reason.length > 0 ? { reason } : {}),
    };
}

export interface ComponentTypeCopy {
    readonly title: string;
    readonly description: string;
    readonly membersLabel: string;
    readonly membersHint: string;
    readonly submitLabel: string;
}

/**
 * What the dialog says for each type. A release says "Flags" where a commercial
 * component says "Members", and says nothing about entitlements: a release may
 * hold a flag of any purpose, so that sentence would be false for it.
 */
export function componentTypeCopy(type: ComponentType): ComponentTypeCopy {
    if (type === 'release') {
        return {
            title: 'New release component',
            description:
                'A release component names the flags that ship together in one release. Creating it changes no flag and grants nothing.',
            membersLabel: 'Flags',
            membersHint:
                'One flag key per line. Flags of any purpose or type are allowed, and the same flag may appear in more than one release. Leave it empty to add flags later.',
            submitLabel: 'Create release',
        };
    }
    return {
        title: 'New component',
        description:
            'A component bundles entitlement flags into one thing Product can name. Creating it grants nothing — no museum gains a flag until it is activated against them.',
        membersLabel: 'Members',
        membersHint:
            'One flag key per line. Every member must be a flag whose purpose is Entitlement — a museum is never billed for a rollout toggle. Leave it empty to assemble the bundle before its flags exist.',
        submitLabel: 'Create component',
    };
}
